import {defineMessages, FormattedMessage, MessageDescriptor} from 'react-intl';
import {view, css, Button} from '@sail/ui/deprecated';
import {useEffect} from 'react';
import {CloseButton} from './CloseButton';
import {useTheme} from '../ThemeProvider';
import {useAnalytics} from '../AnalyticsProvider';

const messages = defineMessages({
  cancelButtonText: {
    id: 'conversations_ui.widget.confirmation.cancelButton.label',
    description:
      'Text shown for the cancel button when presenting a confirmation',
    defaultMessage: 'Cancel',
  },
});

type Props = {
  message: MessageDescriptor | string;
  confirmLabel: MessageDescriptor | string;
  onConfirm: () => void;
  onCancel: () => void;
};

export const Confirmation = ({
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}: Props) => {
  const theme = useTheme();
  const analytics = useAnalytics();

  let promptMessage;
  if (typeof message === 'string') {
    promptMessage = message;
  } else {
    promptMessage = <FormattedMessage {...message} />;
  }

  let confirmLabelMessage;
  if (typeof confirmLabel === 'string') {
    confirmLabelMessage = confirmLabel;
  } else {
    confirmLabelMessage = <FormattedMessage {...confirmLabel} />;
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      analytics.action('confirmation_cancel_clicked_by_pressing_escape');
      onCancel();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  return (
    <view.div
      data-testid="conversations-widget-confirmation"
      css={{
        height: 'fill',
        stack: 'y',
        gap: 'large',
        position: css.value('relative'),
      }}
    >
      <view.h2
        data-testid="conversations-widget-confirmation-message"
        css={{
          margin: 0,
          paddingTop: 400,
          marginRight: 'medium',
          fontSize: css.value('24px'),
          lineHeight: css.value(1.2),
          fontWeight: 'bold',
          color: css.value(theme.textColor),
        }}
      >
        {promptMessage}
      </view.h2>
      <view.div
        css={{
          stack: 'x',
          gap: 'medium',
        }}
      >
        <Button
          data-testid="conversations-widget-confirmation-cancel"
          onPress={() => {
            analytics.action('confirmation_cancel_clicked');
            onCancel();
          }}
          size="large"
          css={{
            color: css.value(theme.textColor),
            '&:focus': {
              outline: theme.focusVisibleOutline,
              focusRing: theme.focusVisibleOutline
                ? css.value('none')
                : undefined,
            },
          }}
        >
          <FormattedMessage {...messages.cancelButtonText} />
        </Button>
        <Button
          data-testid="conversations-widget-confirmation-confirm"
          type="primary"
          size="large"
          onPress={() => {
            analytics.action('confirmation_confirm_clicked');
            onConfirm();
          }}
          css={{
            color: css.value(theme.onCtaTextColor),
            backgroundColor: css.value(theme.ctaColor),
            borderColor: css.value(theme.ctaColor),
            outlineColor: css.value(theme.ctaColor),
            '--button-background-color': css.value(theme.ctaColor),
            '--keyline': css.value(theme.ctaColor),
            keyline: css.value(theme.ctaColor),
            '&:hover': {
              backgroundColor: css.value(theme.ctaHoverColor),
            },
            '&:focus': {
              outline: theme.focusVisibleOutline,
              focusRing: theme.focusVisibleOutline
                ? css.value('none')
                : undefined,
            },
          }}
        >
          {confirmLabelMessage}
        </Button>
      </view.div>
      <CloseButton
        onClick={onCancel}
        dataTestId="support-widget-confirmation-x-button"
      />
    </view.div>
  );
};
